import { mountStoreDevtool } from 'simple-zustand-devtools';
import { create } from 'zustand';

import { BffFacility, CenterLocation } from 'types/location';

type SchedulingStoreState = {
	currentStepIndex: number;
	maxSteps: number;
	selectedTimeslot: string | null;
	isTimeslotsLoading: boolean;

	firstName: string;
	lastName: string;
	phoneNumber: string;
	emailAddress: string;
	optInEmailCheckbox: boolean;
	optInSMSCheckbox: boolean;
	receiveMarketingSMSCheckbox: boolean;
	receiveTransactionalSMSCheckbox: boolean;
	returningGuest: boolean | undefined;

	selectedLocation: CenterLocation | undefined;
	selectedTreatmentId: string;
	selectedTreatmentName: string;
	selectedTreatmentCode: string;

	isUserFlagged: boolean;
	treatmentUpdated: boolean;
	locationUpdated: boolean;

	bffFacilities: BffFacility[] | [];
};

type SchedulingStoreActions = {
	changeCurrentStepIndex: (index: number) => void;
	goToNextStep: () => void;

	setSelectedTimeslot: (selectedTimeslot: string | null) => void;
	setIsTimeslotsLoading: (isTimeslotsLoading: boolean) => void;

	setFirstName: (firstName: string) => void;
	setLastName: (lastName: string) => void;
	setPhoneNumber: (phoneNumber: string) => void;
	setEmailAddress: (emailAddress: string) => void;
	setReceiveEmailCheckbox: (optInEmailCheckbox: boolean) => void;
	setReceiveSMSCheckbox: (optInSMSCheckbox: boolean) => void;
	setReceiveTransactionalSMSCheckbox: (receiveTransactionalSMSCheckbox: boolean) => void;
	setReceiveMarketingSMSCheckbox: (receiveMarketingSMSCheckboxSchema: boolean) => void;
	setReturningGuest: (returningGuest: boolean) => void;
	setSelectedLocation: (location: CenterLocation) => void;
	setSelectedTreatmentId: (treatmentId: string) => void;
	setSelectedTreatmentName: (treatmentName: string) => void;
	setSelectedTreatmentCode: (treatmentCode: string) => void;
	setIsUserFlagged: (isUserFlagged: boolean) => void;
	setTreatmentUpdated: (treatmentUpdated: boolean) => void;
	setLocationUpdated: (locationUpdated: boolean) => void;
	setBffFacilities: (bffFacilities: BffFacility[] | []) => void;
};

const useSchedulingStore = create<SchedulingStoreState & SchedulingStoreActions>()((set, get) => ({
	returningGuest: undefined,
	currentStepIndex: 0,
	maxSteps: 4,
	selectedTimeslot: null,
	isTimeslotsLoading: false,

	firstName: '',
	lastName: '',
	phoneNumber: '',
	emailAddress: '',
	optInEmailCheckbox: false,
	optInSMSCheckbox: true,
	receiveMarketingSMSCheckbox: true,
	receiveTransactionalSMSCheckbox: true,
	selectedLocation: undefined,
	selectedLocationTimezone: '',
	selectedTreatmentId: '',
	selectedTreatmentName: '',
	selectedTreatmentCode: '',
	isUserFlagged: false,
	treatmentUpdated: false,
	locationUpdated: false,
	bffFacilities: [],

	changeCurrentStepIndex: (index) => {
		if (index >= 0 && index <= get().maxSteps - 1) {
			set(() => ({ currentStepIndex: index }));
		}
	},
	setSelectedTimeslot: (selectedTimeslot) => {
		set(() => ({ selectedTimeslot }));
		get().goToNextStep();
	},
	setIsTimeslotsLoading: (isTimeslotsLoading) => {
		set(() => ({ isTimeslotsLoading }));
	},
	setFirstName: (firstName) => {
		set(() => ({ firstName }));
	},
	setLastName: (lastName) => {
		set(() => ({ lastName }));
	},
	setPhoneNumber: (phoneNumber) => {
		set(() => ({ phoneNumber }));
	},
	setEmailAddress: (emailAddress) => {
		set(() => ({ emailAddress }));
	},
	setReceiveEmailCheckbox: (optInEmailCheckbox) => {
		set(() => ({ optInEmailCheckbox }));
	},
	setReceiveSMSCheckbox: (optInSMSCheckbox) => {
		set(() => ({ optInSMSCheckbox }));
	},
	setReceiveTransactionalSMSCheckbox: (receiveTransactionalSMSCheckbox) => {
		set(() => ({ receiveTransactionalSMSCheckbox }));
	},
	setReceiveMarketingSMSCheckbox: (receiveMarketingSMSCheckbox) => {
		set(() => ({ receiveMarketingSMSCheckbox }));
	},
	setReturningGuest: (returningGuest) => {
		set(() => ({ returningGuest }));
		set(() => ({ selectedTreatmentId: '' }));

		get().goToNextStep();
	},
	setSelectedLocation: (location) => {
		set(() => ({ selectedLocation: location }));
		set(() => ({ selectedTreatmentId: '' }));
		get().goToNextStep();
	},
	setSelectedTreatmentId: (treatmentId) => {
		set(() => ({ selectedTreatmentId: treatmentId }));
		get().goToNextStep();
	},
	setSelectedTreatmentName: (treatmentName) => {
		set(() => ({ selectedTreatmentName: treatmentName }));
		get().goToNextStep();
	},
	setSelectedTreatmentCode: (treatmentCode) => {
		set(() => ({ selectedTreatmentCode: treatmentCode }));
	},
	setIsUserFlagged: (flag) => {
		set(() => ({ isUserFlagged: flag }));
	},
	setTreatmentUpdated: (treatmentUpdated) => {
		set(() => ({ treatmentUpdated }));
	},
	setLocationUpdated: (locationUpdated) => {
		set(() => ({ locationUpdated }));
	},
	goToNextStep: () => {
		if (get().returningGuest === undefined) {
			get().changeCurrentStepIndex(0);
			return;
		}

		if (!get().selectedLocation || !get().selectedTreatmentId) {
			get().changeCurrentStepIndex(1);
			return;
		}

		if (!get().selectedTimeslot) {
			get().changeCurrentStepIndex(2);
			return;
		}

		get().changeCurrentStepIndex(3);
	},
	setBffFacilities: (bffFacilities) => {
		set(() => ({ bffFacilities }));
	},
}));

if (process.env.NODE_ENV === 'development') {
	mountStoreDevtool('SchedulingStore', useSchedulingStore);
}

export default useSchedulingStore;
